"use client";

import { ReportDialog } from "@/components/common/dialogs/report-dialog";
import { ListingArchiveUnarchiveAction } from "@/components/listings/more-actions/listing-archive-unarchive-action";
import { useListingDirectPick } from "./use-listing-direct-pick";
import { useMarketplaceWorkspace } from "@/hooks/use-marketplace-workspace";
import { ListingContentDialog } from "@/components/listings/listing-content-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Icon } from "@/components/ui/icon";
import { useCurrentUser } from "@/hooks/use-current-user";
import type { AnyDetailedListing } from "@/lib/types";
import { useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { useState, type ReactNode } from "react";

export type ListingMoreActionsMenuProps = {
  listing: AnyDetailedListing;
  trigger: ReactNode;
  displayViewAsOptions?: boolean;
  onWantsToRefresh?: () => any;
};

export function ListingMoreActionsMenu({
  listing,
  trigger,
  displayViewAsOptions = true,
  onWantsToRefresh,
}: ListingMoreActionsMenuProps) {
  const t = useTranslations();
  const { user, displayAuthDialog } = useCurrentUser();
  const router = useRouter();
  const pathname = usePathname();
  const [contentDialogOpen, setContentDialogOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const marketplace = useMarketplaceWorkspace();
  const principalId = marketplace.data?.principal.id ?? user?.id;

  const isOwnerOrAgent =
    !!user &&
    (listing.ownerId === user.id || listing.sellerAgentId === user.id);

  const slug = listing.slug;
  const showMarketingMaterials = !!slug && !!user?.isAgent;
  const canReportListing = !isOwnerOrAgent;
  const showDirectPick = Boolean(
    marketplace.data?.canDirectPick &&
      listing.ownerId !== principalId &&
      !listing.sellerAgentId &&
      listing.status === "FOR_SALE",
  );
  const directPick = useListingDirectPick({
    listingId: listing.id,
    enabled: showDirectPick && menuOpen,
  });

  const hasAnyMenuItem =
    isOwnerOrAgent || showMarketingMaterials || canReportListing;

  if (!hasAnyMenuItem) {
    return null;
  }

  function refresh() {
    if (onWantsToRefresh) {
      onWantsToRefresh();
    } else {
      router.refresh();
    }
  }

  function viewAsPublic() {
    const url = new URL(pathname, window.location.origin);
    url.searchParams.set("viewAs", "PUBLIC_BUYER");
    router.replace(url.toString());
  }

  function viewAsOwner() {
    const url = new URL(pathname, window.location.origin);
    url.searchParams.delete("viewAs");
    router.replace(url.toString());
  }

  function handleContentClick() {
    if (!user) {
      displayAuthDialog({
        title: "Sign in to access marketing materials",
        onLoggedIn: () => {
          setContentDialogOpen(true);
        },
      });
      return;
    }
    setContentDialogOpen(true);
  }

  function handleReportClick() {
    if (!user) {
      displayAuthDialog({
        title: t("saas.report.dialog.authTitle"),
        onLoggedIn: () => {
          setReportOpen(true);
        },
      });
      return;
    }
    setReportOpen(true);
  }

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>

        <DropdownMenuContent className="w-56" align="end">
          {showDirectPick && directPick.visible && (
            <DropdownMenuItem
              className="min-h-11 cursor-pointer"
              disabled={directPick.isPending}
              onSelect={directPick.select}
            >
              {directPick.label}
            </DropdownMenuItem>
          )}
          {isOwnerOrAgent && displayViewAsOptions && (
            <>
              <DropdownMenuItem
                className="cursor-pointer"
                onSelect={viewAsPublic}
              >
                <Icon icon="Globe1" />
                {t("saas.listing.header.actions.viewAsPublic")}
              </DropdownMenuItem>

              <DropdownMenuItem
                className="cursor-pointer"
                onSelect={viewAsOwner}
              >
                <Icon icon="Lock1" />
                {t("saas.listing.header.actions.viewAsOwner")}
              </DropdownMenuItem>

              <DropdownMenuSeparator />
            </>
          )}

          {showMarketingMaterials && (
            <>
              <DropdownMenuItem
                className="cursor-pointer"
                onSelect={handleContentClick}
              >
                {t("saas.listing.header.actions.createContent")}
              </DropdownMenuItem>

              {isOwnerOrAgent && <DropdownMenuSeparator />}
            </>
          )}

          {isOwnerOrAgent && (
            <ListingArchiveUnarchiveAction
              listing={listing}
              trigger={
                <DropdownMenuItem
                  className="cursor-pointer"
                  onSelect={(e) => e.preventDefault()}
                >
                  <Icon
                    icon={listing.status === "ARCHIVED" ? "Recycle" : "Trash2"}
                  />
                  {listing.status === "ARCHIVED"
                    ? t("saas.listing.header.actions.unarchive")
                    : t("saas.listing.header.actions.archive")}
                </DropdownMenuItem>
              }
              onArchivedChanged={refresh}
            />
          )}

          {canReportListing && (
            <>
              {(isOwnerOrAgent || showMarketingMaterials) && (
                <DropdownMenuSeparator />
              )}
              <DropdownMenuItem
                className="cursor-pointer"
                onSelect={() => {
                  handleReportClick();
                }}
              >
                <Icon icon="WarningSolid" />
                {t("saas.listing.header.actions.report")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {showMarketingMaterials && (
        <ListingContentDialog
          slug={slug}
          open={contentDialogOpen}
          onOpenChange={setContentDialogOpen}
          isStaff={!!user?.isStaff}
        />
      )}

      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        target={{ type: "LISTING", listingId: listing.id }}
      />
    </>
  );
}

ListingMoreActionsMenu.displayName = "ListingMoreActionsMenu";
